'use client'

import React from 'react'
import { cn } from '@/lib/utils'
import { API_CONFIG } from '@/config/api'
import { getImageCandidates } from '@/lib/image'

export function InviteHeroBadge({
  inviterName,
  inviterAvatarUrl,
  communityName,
  communityAvatarUrl,
  className,
}: {
  inviterName: string
  inviterAvatarUrl?: string | null
  communityName: string
  communityAvatarUrl?: string | null
  className?: string
}) {
  const inviterInitials = getInitials(inviterName)
  const communityInitials = getInitials(communityName)

  const inviterAvatarCandidates = React.useMemo(
    () =>
      getImageCandidates(inviterAvatarUrl, {
        width: 96,
        height: 96,
        siteOrigin: API_CONFIG.BASE_URL,
        assetBaseUrl: process.env.NEXT_PUBLIC_ASSET_BASE_URL,
      }),
    [inviterAvatarUrl]
  )

  const communityAvatarCandidates = React.useMemo(
    () =>
      getImageCandidates(communityAvatarUrl, {
        width: 96,
        height: 96,
        siteOrigin: API_CONFIG.BASE_URL,
        assetBaseUrl: process.env.NEXT_PUBLIC_ASSET_BASE_URL,
      }),
    [communityAvatarUrl]
  )

  const [avatarInviter, setAvatarInviter] = React.useState(0)
  const [avatarCommunity, setAvatarCommunity] = React.useState(0)

  React.useEffect(() => {
    setAvatarInviter(0)
  }, [inviterAvatarUrl])

  React.useEffect(() => {
    setAvatarCommunity(0)
  }, [communityAvatarUrl])

  const resolvedInviterAvatarSrc = inviterAvatarCandidates[avatarInviter] || ''
  const resolvedCommunityAvatarSrc = communityAvatarCandidates[avatarCommunity] || ''
  
  //console.log('resolvedAvatar: ', resolvedInviterAvatarSrc)

  return (
    <div className={cn('text-center', className)}>
      {/* Avatars */}
      <div className="flex items-center justify-center">
        <div className="relative h-24 w-[180px]">
          {/* left avatar */}
          <div className="absolute left-0 top-0">
            <CircleAvatar
              src={resolvedInviterAvatarSrc}
              initials={inviterInitials}
              ring="ring-fuchsia-400/40"
              onError={() => setAvatarInviter((i) => i + 1)}
            />
          </div>

          {/* right avatar (slightly behind) */}
          <div className="absolute right-0 top-0">
            <CircleAvatar
              src={resolvedCommunityAvatarSrc}
              initials={communityInitials}
              ring="ring-sky-400/35"
              onError={() => setAvatarCommunity((i) => i + 1)}
            />
          </div>

          {/* overlap glow */}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="h-16 w-16 rounded-full bg-gradient-to-r from-fuchsia-500/10 to-sky-500/10 blur-xl" />
          </div>
        </div>
      </div>

      {/* Text */}
      <div className="mt-4">
        <div className="text-2xl font-semibold tracking-tight">
          {inviterName} te invitó
        </div>
        <div className="mt-1 text-sm text-muted-foreground">
          a unirte a la comunidad <span className="text-foreground/90">{communityName}</span>
        </div>
      </div>
    </div>
  )
}

function CircleAvatar({
  src,
  initials,
  ring,
  onError,
}: {
  src?: string | null
  initials: string
  ring?: string
  onError?: () => void
}) {
  return (
    <div
      className={cn(
        'h-24 w-24 rounded-full bg-background',
        'ring-4 ring-background shadow-sm'
      )}
    >
      <div
        className={cn(
          'h-24 w-24 rounded-full overflow-hidden border border-border',
          'ring-2',
          ring
        )}
      >
        {src ? (
          <img
            src={src}
            alt=""
            className="h-full w-full object-cover"
            onError={onError}
          />
        ) : (
          <div className="h-full w-full bg-muted flex items-center justify-center text-lg font-semibold text-foreground">
            {initials}
          </div>
        )}
      </div>
    </div>
  )
}

function getInitials(name: string) {
  return (name || '')
    .trim()
    .split(/\s+/)
    .map(s => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
}
